import {
  Checkbox,
  MultiSelect,
  NumberInput,
  PasswordInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import _ from "lodash";
import { resolveDateInputFormat } from "./dateInputFormat";
import OccupationSelect from "../components/occupationSelect";

const EMPTY_DISPLAY = "-";

const PRIMITIVE_KIND_BY_COMPONENT = new Map([
  [TextInput, "text"],
  [Textarea, "textarea"],
  [NumberInput, "number"],
  [Select, "select"],
  [OccupationSelect, "select"],
  [MultiSelect, "multiselect"],
  [DatePicker, "date"],
  [TimeInput, "time"],
  [Switch, "switch"],
  [Checkbox, "checkbox"],
  [PasswordInput, "password"],
]);

const EXTRA_FIELD_KIND = {
  TEXTAREA: "textarea",
  INTEGER: "number",
  DECIMAL_2: "number",
  DECIMAL_4: "number",
  DATE: "date",
  DATETIME: "datetime",
  SELECT: "select",
  BOOLEAN: "switch",
  TEXT: "text",
};

export const isEmptyFormValue = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const getFormFieldDisplayKind = (component) =>
  PRIMITIVE_KIND_BY_COMPONENT.get(component) ?? null;

export const getExtraFieldDisplayKind = (fieldType) => {
  if (typeof fieldType === "string" && fieldType.startsWith("REF_")) {
    return "remoteSelect";
  }
  return EXTRA_FIELD_KIND[fieldType] ?? "text";
};

const translateLabel = (label, t) => {
  if (label == null || label === "") return label;
  return t ? t(label) : label;
};

const flattenSelectData = (data) => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((item) => {
    if (typeof item === "string") return [{ value: item, label: item }];
    if (item?.items) return flattenSelectData(item.items);
    return [item];
  });
};

const formatSelectLabel = (value, data, t) => {
  const options = flattenSelectData(data);
  const match = options.find((item) => item?.value === value);
  const label = match?.label ?? value;
  return translateLabel(label, t);
};

const formatNumber = (value, props = {}) => {
  if (typeof props.formatter === "function") {
    return String(props.formatter(String(value)));
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const precision = props.precision;
  const text =
    precision == null || precision === undefined
      ? String(num)
      : num.toFixed(precision);
  if (!props.thousandSeparator) return text;
  const [intPart, decPart] = text.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, props.thousandSeparator);
  return decPart != null ? `${grouped}.${decPart}` : grouped;
};

const formatDate = (value, props = {}, locale = "en") => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_DISPLAY;
  const format = resolveDateInputFormat(props);
  const localeKey = locale === "en" ? "en" : locale;
  return dayjs(date).locale(localeKey).format(format);
};

const formatTime = (value, locale = "en") => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_DISPLAY;
  return dayjs(date).locale(locale === "en" ? "en" : locale).format("HH:mm");
};

const formatRemoteSelect = (value, props = {}) => {
  if (Array.isArray(value)) {
    const labels = value
      .map((item) => formatRemoteSelect(item, props))
      .filter((item) => item && item !== EMPTY_DISPLAY);
    return labels.length ? labels.join(", ") : EMPTY_DISPLAY;
  }
  if (value && typeof value === "object") {
    if (typeof props.labelRender === "function") {
      return props.labelRender(value) || EMPTY_DISPLAY;
    }
    if (props.labelField) {
      return _.get(value, props.labelField) || EMPTY_DISPLAY;
    }
    return (
      value.name ||
      value.label ||
      value.code ||
      String(value._id ?? EMPTY_DISPLAY)
    );
  }
  return String(value);
};

export const formatFormFieldDisplay = (value, options = {}) => {
  const { kind = "text", props = {}, t = (s) => s, locale = "en" } = options;

  if (kind === "password") {
    return isEmptyFormValue(value) ? EMPTY_DISPLAY : "••••";
  }
  if (kind === "switch" || kind === "checkbox") {
    return t(value ? "Yes" : "No");
  }
  if (isEmptyFormValue(value)) return EMPTY_DISPLAY;

  if (kind === "select") {
    return formatSelectLabel(value, props.data, t);
  }
  if (kind === "multiselect") {
    const values = Array.isArray(value) ? value : [value];
    return values.map((item) => formatSelectLabel(item, props.data, t)).join(", ");
  }
  if (kind === "number") {
    return formatNumber(value, props);
  }
  if (kind === "date") {
    return formatDate(value, props, locale);
  }
  if (kind === "datetime") {
    const dateText = formatDate(value, props, locale);
    if (dateText === EMPTY_DISPLAY) return EMPTY_DISPLAY;
    return `${dateText} ${formatTime(value, locale)}`;
  }
  if (kind === "time") {
    return formatTime(value, locale);
  }
  if (kind === "remoteSelect") {
    return formatRemoteSelect(value, props);
  }
  return String(value);
};
